import type { DispatchedJob } from '@whittlelabs/jobs';
import {
  renderPromptExecution,
  readPromptExecutionSpec,
  pickProviderHints,
  PROMPT_EXECUTION_STRATEGY,
} from '@whittlelabs/loom';
import type { CostHint, ExecutorCapability, ExecutorResponse, JobExecutor } from './types';
import { assertOutputValid } from './validate-output';

/**
 * Webhook executor: POSTs the rendered request to a configured URL and expects a
 * `{ rawText, parsed?, usage? }` JSON body in return.
 */
interface WebhookConfig {
  url: string;
  headers: Record<string, string>;
  timeout: number;
  capabilityId: string;
}

const DEFAULT_TIMEOUT = 30_000;

interface WebhookOutput {
  rawText?: string;
  parsed?: Record<string, unknown>;
  usage?: ExecutorResponse['usage'];
}

class WebhookExecutor implements JobExecutor {
  readonly capability: ExecutorCapability;
  readonly strategies = [PROMPT_EXECUTION_STRATEGY];
  private readonly config: WebhookConfig;

  constructor(config: WebhookConfig) {
    this.config = config;
    this.capability = { id: config.capabilityId };
  }

  estimate(dispatch: DispatchedJob): CostHint {
    const spec = readPromptExecutionSpec(dispatch.inputs);
    return {
      inputTokens: spec.costCapHint?.estimatedInputTokens,
      outputTokens: spec.costCapHint?.estimatedOutputTokens,
    };
  }

  async execute(dispatch: DispatchedJob, signal: AbortSignal): Promise<ExecutorResponse> {
    const { prompt, spec, artifacts } = renderPromptExecution(dispatch.inputs);
    const outputLabel = spec.outputLabel;
    const request = {
      prompt,
      outputSchema: spec.outputSchema,
      outputLabel,
      providerHints: pickProviderHints(spec, this.config.capabilityId),
      artifacts,
    };

    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), this.config.timeout);
    function onAbort(): void {
      timeoutController.abort();
    }
    signal.addEventListener('abort', onAbort, { once: true });

    try {
      const response = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          ...this.config.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: timeoutController.signal,
      });

      if (!response.ok) {
        let body = '';
        try {
          body = await response.text();
        } catch {
          // ignore
        }
        throw new Error(`Webhook returned ${response.status}${body ? `: ${body}` : ''}`);
      }

      const data = (await response.json()) as WebhookOutput;
      if (!data || typeof data !== 'object' || typeof data.rawText !== 'string') {
        throw new Error('Webhook response missing required "rawText" string field');
      }
      assertOutputValid(spec, data.parsed);
      return {
        outputs: [{ label: outputLabel, content: data.parsed ?? { raw: data.rawText } }],
        rawText: data.rawText,
        parsed: data.parsed,
        usage: data.usage,
      };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }
}

export function createWebhookExecutor(
  config: Record<string, unknown>,
  capabilityId = 'webhook',
): WebhookExecutor {
  const url = config.url;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error('WebhookExecutor requires a non-empty "url" string in config');
  }

  let headers: Record<string, string> = {};
  if (config.headers !== undefined) {
    if (
      config.headers === null ||
      typeof config.headers !== 'object' ||
      Array.isArray(config.headers)
    ) {
      throw new Error('"headers" must be an object');
    }
    for (const [key, value] of Object.entries(config.headers)) {
      if (typeof key !== 'string' || typeof value !== 'string') {
        throw new Error('All header keys and values must be strings');
      }
    }
    headers = config.headers as Record<string, string>;
  }

  let timeout = DEFAULT_TIMEOUT;
  if (config.timeout !== undefined) {
    if (typeof config.timeout !== 'number' || config.timeout <= 0) {
      throw new Error('"timeout" must be a positive number');
    }
    timeout = config.timeout;
  }

  return new WebhookExecutor({ url, headers, timeout, capabilityId });
}
